import { MARKETPLACE_TIERS } from '@onward-delivery/core';
import { buildRouteColors } from './util/routeColors';

export const MAX_ROUTES = 50;

export const SELECTED_COLOR = '#59B863';

export const UNASSIGNED_COLOR = '#2B2B2B';

export const SCHEDULED_UNASSIGNED_COLOR = '#E28700';

export const ROUTE_COLORS = buildRouteColors();

export const MODALS = {
    COMMUNICATIONS: 'COMMUNICATIONS',
    ORDER_EDIT: 'ORDER_EDIT',
    ORDER_OMS: 'ORDER_OMS',
    ORDER_MARKETPLACE: 'ORDER_MARKETPLACE',
    ORDER_RESCHEDULE: 'ORDER_RESCHEDULE',
    BATCH_TIMEFRAME_CONFIRMATION: 'BATCH_TIMEFRAME_CONFIRMATION',
    ORDER_UNASSIGNED_MARKETPLACE: 'ORDER_UNASSIGNED_MARKETPLACE',
    ORDER_VIEW: 'ORDER_VIEW',
    ROUTE_EXPORT: 'ROUTE_EXPORT',
    ROUTE_INTERNAL: 'ROUTE_INTERNAL',
    ROUTE_MARKETPLACE: 'ROUTE_MARKETPLACE',
    ROUTE_REMOVE: 'ROUTE_REMOVE',
    ROUTE_REMOVE_STOP: 'ROUTE_REMOVE_STOP',
    ROUTE_RESCHEDULE: 'ROUTE_RESCHEDULE',
    ROUTE_UNLOCK: 'ROUTE_UNLOCK',
    SENT_TO_ONWARD: 'SENT_TO_ONWARD',
    CLAIM_TC: 'CLAIM_TC',
    PLACE_BID: 'PLACE_BID',
    DRIVER_OVERLAP: 'DRIVER_OVERLAP',
    SETTINGS: 'SETTINGS',
    DRIVER_LUNCH: 'DRIVER_LUNCH',
    CUSTOM_TIMEFRAME: 'CUSTOM_TIMEFRAME',
    NOTES: 'NOTES',
    STOP_WINDOW: 'STOP_WINDOW',
    AWAITING_CD: 'AWAITING_CD',
    ORDERS_OFF_RESOURCE: 'ORDERS_OFF_RESOURCE',
};

export const TEMP_ROUTE = {
    orders: [],
    stopsByRouteId: [],
    route_id: '13133fcd-8316-45ec-bd63-7f356e71de44',
    bulk_bol: null,
    carrier_id: null,
    driver_id: null,
    helper_id: null,
    legacy_carrier_id: null,
    claimed_at: null,
    complete: null,
    completed: null,
    created_at: new Date().toISOString(),
    created_by: null,
    delivery_by: null,
    legacy_driver_id: null,
    estimated_driving_distance: null,
    estimated_driving_time: null,
    finish_returns_next_day: null,
    geo_timestamps: null,
    handle_orders_on_delete: null,
    is_new_route: null,
    last_modified_at: new Date().toISOString(),
    last_modified_by: null,
    last_modified_source: null,
    last_update_source: null,
    legacy_route_id: null,
    legacy_shipper_id: null,
    margin: null,
    need_to_optimize: true,
    need_to_submit: false,
    needs_verification: false,
    oms: null,
    pending_confirmations: null,
    pickup_time: null,
    planning: true,
    route_number: Infinity,
    scheduled_delivery: null,
    sending_to: null,
    source_form: 'PLANNING',
    start_time: null,
    status: 'planning',
    test_acc: null,
    total_carrier_rate: null,
    total_cubes: null,
    total_shipper_rate: null,
    resource_id: null,
    route_alias: null,
    driver_id_final_return: null,
    route_carrier: null,
    route_shipper: null,
    staging_verification: null,
    __typename: 'routes',
};

// Null-filled StopFields template. Client-built stops only carry the fields
// FTLStopHelpers sets (address/lat/lng/orders/ordering/type); optimistic cache
// writes must be StopFields-complete or the planning query read goes partial.
export const TEMP_STOP = {
    actions_completed: null,
    address: null,
    arrival_time: null,
    del_window_buffer: null,
    del_window_delay: null,
    del_window_end: null,
    del_window_rounded: null,
    del_window_start: null,
    driving_time: null,
    driving_distance: null,
    earliest_start: null,
    end: null,
    exchanges: null,
    geo_fence_enter: null,
    geo_fence_exit: null,
    lat: null,
    latest_end: null,
    legacy_orders: null,
    legacy_route_id: null,
    lng: null,
    ordering: null,
    orders: null,
    overnight: null,
    pinned: null,
    returns: null,
    route_id: null,
    service_time: null,
    start: null,
    stop_id: null,
    stop_completion_time: null,
    stop_end_time: null,
    stop_start_time: null,
    type: null,
    wait_time: null,
    __typename: 'stops',
};

const MARKETPLACE_TIER_LIST = [MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING];

// Client-side predicates for the Plan page category filters. Each category is
// a multi-select: options within a category are combined with OR, categories
// are combined with AND. The ALL option is the wildcard (predicate === null).
// Consumers read these via the filteredOrders memo on PlanningContext.
export const ORDER_FILTER_OPTIONS = {
    ORDER_TYPE: {
        ALL: { label: 'All', predicate: null },
        DELIVERY: { label: 'Delivery', predicate: (o) => o.order_type === 'delivery' },
        RETURN: { label: 'Return', predicate: (o) => o.order_type === 'return' },
        EXCHANGE: { label: 'Exchange', predicate: (o) => o.order_type === 'exchange' },
        SERVICE: { label: 'Service Callback', predicate: (o) => o.order_type === 'service' },
    },
    WAREHOUSING_TYPE: {
        ALL: { label: 'All', predicate: null },
        PICKUP_AND_DELIVERY: {
            label: 'Pickup and Delivery',
            predicate: (o) => !(o.wh_events || []).some((e) => e.action === 'START:AWAITING'),
        },
        RECEIVED: {
            label: 'Received',
            predicate: (o) => {
                const events = o.wh_events || [];
                return events.some((e) => e.action === 'START:AWAITING') && events.some((e) => e.status === 'RECEIVED');
            },
        },
        INBOUND: {
            label: 'Inbound',
            predicate: (o) => {
                const events = o.wh_events || [];
                return (
                    events.some((e) => e.action === 'START:AWAITING') && !events.some((e) => e.status === 'RECEIVED')
                );
            },
        },
        CROSS_DOCK: {
            label: 'Cross Dock',
            predicate: (o) => (o.wh_events || []).some((e) => e?.transition?.next === 'CD_PENDING_PO'),
        },
    },
    // The server always narrows to [scheduled in day window] ∪ [unscheduled if
    // the user has plan-page-unscheduled], so these predicates just check the
    // presence of a date on the side that matches the order's crossdock_leg.
    // This category's combobox should only render for users with the circle;
    // PlanningOrders.js handles that gating.
    SCHEDULING_TYPE: {
        ALL: { label: 'All', predicate: null },
        SCHEDULED: {
            // Scheduled excludes missed orders so the two options are mutually
            // exclusive. `_isMissed` is computed in context.js (it needs the plan
            // date, which predicates don't have).
            label: 'Scheduled',
            predicate: (o) => !o._isMissed && (o.crossdock_leg === 'pickup' ? !!o.pickup_date : !!o.delivery_date),
        },
        MISSED: {
            label: 'Missed Orders',
            predicate: (o) => !!o._isMissed,
        },
        UNSCHEDULED: {
            label: 'Unscheduled',
            predicate: (o) => (o.crossdock_leg === 'pickup' ? !o.pickup_date : !o.delivery_date),
        },
    },
    MARKETPLACE_TYPE: {
        ALL: { label: 'All', predicate: null },
        MARKETPLACE: {
            label: 'Marketplace',
            predicate: (o) => !o.oms && MARKETPLACE_TIER_LIST.includes(o?.shipper_rate_override?.marketplace_tier),
        },
        INTERNAL: {
            label: 'Internal',
            predicate: (o) => !!o.oms || MARKETPLACE_TIER_LIST.includes(o?.shipper_rate_override?.marketplace_tier),
        },
    },
    TAG_TYPE: {
        ALL: { label: 'All', predicate: null },
        VIP: {
            label: 'VIP',
            predicate: (o) => (o?.tags || []).some(({ tag }) => tag.tag?.toLowerCase() === 'vip'),
        },
    },
};
